export type StockActionRuntimeItem = {
  reason_definition_id: string;
  reason_code: string;
  label: string;
  description: string | null;
  effect_class: string;
  allowed_direction: "INCREASE" | "DECREASE" | "BOTH" | string;
  requires_note: boolean;
  risk_level: string;
  required_permission_code: string | null;
  available: boolean;
  execution_mode: "ONLINE_REQUIRED" | "DISABLED" | string;
  offline_authority_status: string;
  blocked_reason: string | null;
  changed_at: string;
};

export type StockActionRuntimePayload = {
  ok: boolean;
  contract: "GET_POS_STOCK_ACTION_RUNTIME_V1" | string;
  owner: "MORE_STOCK" | string;
  organization_id: string;
  branch_id: string;
  mode: "FULL" | "DELTA" | "DISABLED" | string;
  items: StockActionRuntimeItem[];
  next_cursor: null | { changed_at: string | null; entity_id: string | null };
  has_more: boolean;
  server_at: string;
};

export type StockActionCommitResult = {
  pos_stock_action_contract?: "POST_POS_STOCK_OPERATIONAL_ACTION_V1" | "POST_POS_STOCK_INTERNAL_USE_BATCH_V1";
  operational_action_contract?: string;
  result_status?: string;
  batch_id?: string;
  reason_code?: string;
  reason_label?: string;
  effect_class?: string;
  required_permission_code?: string;
  execution_mode?: string;
  business_line_count?: number;
  note?: string | null;
  movements?: Array<Record<string, unknown>>;
};

type GatewayEnvelope<T> = { ok: boolean; data?: T; error?: string };

export class PosStockActionGatewayClient {
  private readonly baseUrl: string;
  private readonly timeoutMs: number;

  constructor(options: { baseUrl: string; timeoutMs?: number }) {
    this.baseUrl = options.baseUrl.replace(/\/+$/, "");
    this.timeoutMs = options.timeoutMs ?? 15_000;
  }

  private async post<T>(path: string, body: Record<string, unknown>) {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), this.timeoutMs);
    try {
      const response = await fetch(`${this.baseUrl}${path}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "omit",
        body: JSON.stringify(body),
        signal: controller.signal,
      });
      const payload = (await response.json()) as GatewayEnvelope<T>;
      if (!response.ok || !payload.ok || payload.data == null) {
        return { ok: false as const, data: null, error: payload.error ?? `GATEWAY_HTTP_${response.status}` };
      }
      return { ok: true as const, data: payload.data, error: null };
    } catch (error) {
      return {
        ok: false as const,
        data: null,
        error: error instanceof DOMException && error.name === "AbortError" ? "GATEWAY_TIMEOUT" : "GATEWAY_UNREACHABLE",
      };
    } finally {
      window.clearTimeout(timeout);
    }
  }

  loadRuntime(input: { deviceSecret: string; sessionToken: string }) {
    return this.post<StockActionRuntimePayload>("/api/pos/runtime/local-projections", {
      device_secret: input.deviceSecret,
      session_token: input.sessionToken,
      domain: "STOCK_ACTION",
      cursor: null,
      limit: 100,
    });
  }

  commit(input: {
    deviceSecret: string;
    sessionToken: string;
    stockItemId: string;
    quantityDelta: number;
    reasonCode: string;
    note: string | null;
    requestKey: string;
    clientContext?: Record<string, unknown>;
  }) {
    return this.post<StockActionCommitResult>("/api/pos/runtime/stock-action/commit", {
      device_secret: input.deviceSecret,
      session_token: input.sessionToken,
      stock_item_id: input.stockItemId,
      quantity_delta: input.quantityDelta,
      reason_code: input.reasonCode,
      note: input.note,
      request_key: input.requestKey,
      client_context: input.clientContext ?? {},
    });
  }

  commitInternalUseBatch(input: {
    deviceSecret: string;
    sessionToken: string;
    lines: Array<{ stockItemId: string; quantity: number }>;
    note: string | null;
    requestKey: string;
    clientContext?: Record<string, unknown>;
  }) {
    return this.post<StockActionCommitResult>("/api/pos/runtime/stock-action/commit", {
      device_secret: input.deviceSecret,
      session_token: input.sessionToken,
      lines: input.lines.map((line) => ({ stock_item_id: line.stockItemId, quantity: line.quantity })),
      note: input.note,
      request_key: input.requestKey,
      client_context: input.clientContext ?? {},
    });
  }
}
